import * as React from 'react';
import { Dialog as SheetPrimitive } from '@base-ui/react/dialog';

import {
  SIDE_BODY,
  SIDE_CLOSE,
  SIDE_DESCRIPTION,
  SIDE_FOOTER,
  SIDE_HEADER,
  SIDE_SAFE_AREA,
  SIDE_SIZE,
  SIDE_TITLE,
  type SideSize,
} from '../lib/side-surface';
import { cn } from '../lib/utils';
import { ModalRoot, useForceVeil, useShowsVeil } from '../lib/modal-veil';
import { Button } from './button';
import { XIcon } from '@phosphor-icons/react';

function Sheet({ ...props }: Readonly<SheetPrimitive.Root.Props>) {
  return (
    <ModalRoot.Provider value={props.modal ?? true}>
      <SheetPrimitive.Root data-slot="sheet" {...props} />
    </ModalRoot.Provider>
  );
}

function SheetTrigger({ ...props }: Readonly<SheetPrimitive.Trigger.Props>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: Readonly<SheetPrimitive.Close.Props>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: Readonly<SheetPrimitive.Portal.Props>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({
  className,
  forceRender,
  ...props
}: Readonly<SheetPrimitive.Backdrop.Props>) {
  const force = useForceVeil();
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        // VOLL: `genui2` aqui é só pelo `--z-popup` — o véu não usa token de cor
        // (preto a 10%), mas sai por Portal e sem a classe a variável não resolve.
        'genui2',
        'fixed inset-0 z-(--z-popup) bg-black/10 transition-opacity duration-150 data-ending-style:opacity-0 data-starting-style:opacity-0 supports-backdrop-filter:backdrop-blur-xs',
        className,
      )}
      // VOLL: veil by default even nested — see `DialogOverlay`.
      forceRender={forceRender ?? force}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = 'right',
  size = 'narrow',
  showCloseButton = true,
  closeLabel = 'Close',
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** A régua das superfícies laterais (`lib/side-surface.ts`), só para `left`/`right`: tela
   *  inteira abaixo de `sm`, e a partir dele 448/576/768. `top`/`bottom` ocupam a largura. */
  size?: SideSize;
  showCloseButton?: boolean;
  /** Nome acessível do botão de fechar; consumidores de superfície de cliente passam a copy
   *  traduzida. */
  closeLabel?: string;
}) {
  const vertical = side === 'left' || side === 'right';
  const veil = useShowsVeil();
  return (
    <SheetPortal>
      {veil && <SheetOverlay />}
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-side={side}
        data-size={size}
        className={cn(
          // VOLL: `genui2` obrigatório — o Popup sai por Portal no <body>, fora
          // da raiz `.genui2`, e sem a classe perde os tokens E o preflight
          // escopado (sintoma real: sidebar mobile abria com a fonte do CSS
          // legado do host, bolinhas nos <li> e fundo transparente).
          'genui2',
          'fixed z-(--z-popup) flex flex-col gap-4 bg-popover bg-clip-padding text-sm text-popover-foreground shadow-lg transition duration-200 ease-in-out motion-reduce:transition-none data-ending-style:opacity-0 data-starting-style:opacity-0 data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=bottom]:data-ending-style:translate-y-[2.5rem] data-[side=bottom]:data-starting-style:translate-y-[2.5rem] data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:sm:border-r data-[side=left]:data-ending-style:translate-x-[-2.5rem] data-[side=left]:data-starting-style:translate-x-[-2.5rem] data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:sm:border-l data-[side=right]:data-ending-style:translate-x-[2.5rem] data-[side=right]:data-starting-style:translate-x-[2.5rem] data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=top]:data-ending-style:translate-y-[-2.5rem] data-[side=top]:data-starting-style:translate-y-[-2.5rem]',
          vertical && SIDE_SIZE[size],
          className,
          // O recorte do aparelho nas bordas que a gaveta encosta (#4329). DEPOIS do `className`:
          // é invariante, e um `p-0` do consumidor (a sidebar mobile) o apagaria no `cn`.
          vertical && SIDE_SAFE_AREA,
          side === 'top' && 'pt-[env(safe-area-inset-top,0px)]',
          side === 'bottom' && 'pb-[env(safe-area-inset-bottom,0px)]',
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close
            data-slot="sheet-close"
            render={
              <Button
                variant="ghost"
                // Na gaveta de baixo o topo não encosta na status bar: o ✕ fica no lugar.
                className={side === 'bottom' ? 'absolute top-3 right-3' : SIDE_CLOSE}
                size="icon-sm"
              />
            }
          >
            <XIcon />
            <span className="sr-only">{closeLabel}</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  );
}

/* VOLL: **calha lateral de 24px, o piso de qualquer drawer** (chamada do designer). Cabeçalho, corpo
   e rodapé vêm da mesma régua do `SidePanel` (`lib/side-surface.ts`): todo drawer do repo herda o piso
   em vez de cada gaveta redescobri-lo. */
function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-header" className={cn(SIDE_HEADER, className)} {...props} />;
}

/** O miolo que rola, na calha da gaveta — publica `--page-gutter` para um `page-bleed` dentro dele. */
function SheetBody({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="sheet-body" className={cn(SIDE_BODY, className)} {...props} />;
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-footer"
      data-side-footer=""
      className={cn(SIDE_FOOTER, className)}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: Readonly<SheetPrimitive.Title.Props>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      // VOLL: sem `font-heading` — Fraunces é decorativa e exige aprovação do designer; Geist vale
      // no restante. O preset a colocava em todo título de sheet.
      className={cn(SIDE_TITLE, className)}
      {...props}
    />
  );
}

function SheetDescription({ className, ...props }: Readonly<SheetPrimitive.Description.Props>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn(SIDE_DESCRIPTION, className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
